import React from "react";
import api from "../../axiosConfig";
import { PublicClientApplication, AuthenticationResult } from "@azure/msal-browser";
import { authConfig, loginRequest, silentRequest } from "./authConfig";
import LoginScreen from "../LoginScreen/LoginScreen";
import { AppProps } from "../../App";

const publicClientApplication = new PublicClientApplication(authConfig);

export const logout = (): void => {
  localStorage.clear();
  publicClientApplication.logout();
};

export interface LoginState {
  loginStatus?: loginStatusOptions;
  errorMsg?: string;
}

export type loginStatusOptions =
  | "Redirecting to login"
  | "Logged In"
  | "Authenticating user"
  | "Login Error"
  | "Bypass"
  | "Prompt";

// eslint-disable-next-line
export function AuthProvider(WrappedComponent: React.FC<AppProps>): any {
  // eslint-disable-next-line
  return class extends React.Component<LoginState> {
    state: LoginState = { loginStatus: "Prompt" };

    /**
     * Initiates the login flow for the user
     */
    login = async (): Promise<void> => {
      try {
        await publicClientApplication.loginRedirect(loginRequest);
      } catch (e) {
        console.error("Login error: " + e);
        this.setState({
          loginStatus: "Login Error",
          errorMsg: "Failed to login"
        });
      }
    };

    /**
     * Handles the result of the login response
     * @param {AuthenticationResult | null} response A response object from the AAD login
     */
    handleAuthenticationResult = async (response: AuthenticationResult | null): Promise<void> => {
      if (response?.accessToken) {
        // Add access token to every axios request header
        api.defaults.headers.common.Authorization = "Bearer " + response.accessToken;
        this.setState({ loginStatus: "Logged In" });
      }
    };

    /**
     * Tries to login user with active session silenty.
     * User is prompted to re-enter the credentials if the session is expired or expires soon
     */
    trySilentLogin = async (): Promise<void> => {
      const accounts = publicClientApplication.getAllAccounts();
      // Currently supports silent login for only one session at a time
      if (accounts?.length === 1) {
        const username = accounts[0].username;
        try {
          // See: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/login-user.md#silent-login-with-ssosilent
          const loginResponse: AuthenticationResult = await publicClientApplication.ssoSilent(
            silentRequest(username)
          );
          await this.handleAuthenticationResult(loginResponse);
        } catch (error) {
          // We seem to end up here, when we are falsely trying to use MSAL account that has been changed in another browser window.
          // The error parameter in catch seems always to be undefined (or invalid), as it can not be used even for comparing!
          // So, no error handling here, just provide user the error page with logout button, for avoiding login redirect loop.
          this.setState({
            loginStatus: "Login Error",
            errorMsg: "Something went wrong during the login. Please try again later"
          });
        }
      }
    };

    async componentDidMount(): Promise<void> {
      // If aad config is not set, bypass AAD login. Pdapi does the real authentication, so this does not allow access to any data
      // However, this can be removed when aad auth is working
      if ((process.env.REACT_APP_APPID || "") === "") {
        this.setState({ loginStatus: "Bypass" });
      } else {
        // Handler for the loginRedirect. Not my favorite way of doing things but
        // it is required because of React component lifecycles.
        if (localStorage.getItem("token") != null) {
          const token: string | null = localStorage.getItem("token");
          if (token) {
            this.handleAuthenticationResult(JSON.parse(token));
          }
        } else {
          publicClientApplication
            .handleRedirectPromise()
            .then(async (tokenResponse: null | AuthenticationResult) => {
              if (tokenResponse != null) {
                localStorage.setItem("token", JSON.stringify(tokenResponse));
              }
              this.handleAuthenticationResult(tokenResponse);
            })
            .catch(error => {
              console.error("error: " + error);
            });
        }
      }
    }

    render(): JSX.Element {
      if (this.state.loginStatus === "Logged In" || this.state.loginStatus === "Bypass") {
        return (
          <WrappedComponent
            {...this.props}
            bypassAuthorization={this.state.loginStatus === "Bypass"}
          />
        );
      } else if (this.state.loginStatus === "Login Error") {
        return (
          <div>
            <p>Login error. Please try again.</p>
            <p>{this.state.errorMsg}</p>
            <button onClick={() => logout()}>Log out</button>
          </div>
        );
      } else {
        return (
          <LoginScreen
            loginStatus={this.state.loginStatus || "Prompt"}
            onLoginClick={() => this.login()}
          />
        );
      }
    }
  };
}
